
import { getSupabaseClient, normalizeSupabaseError } from "./supabaseClient.js";

// Mapping to the table name created by the user
const TABLE_NAME = "emails";

// El envío de correo desde el CRM está desactivado: el equipo escribe desde Outlook
// y la Edge Function send-email se retiró (auditoría de seguridad, 26/10/2026).
// La recepción (incoming-email / process-email) sigue funcionando.
export const EMAIL_SENDING_ENABLED = false;

// Remitente para la comunicación INTERNA al equipo (invites, mensajes).
// Debe ser un dominio/subdominio VERIFICADO en Resend. wearelostoldos.com no lo está;
// el verificado es inbound.wearelostoldos.com.
const INTERNAL_FROM = "Los Toldos <lead@inbound.wearelostoldos.com>";

// =========================
// Helpers
// =========================
function assertId(id, name = "id") {
    if (id === null || id === undefined || String(id).trim() === "") {
        throw new Error(`${name} es obligatorio.`);
    }
}

function throwSupabase(error, context = "") {
    if (!error) return;
    const n = normalizeSupabaseError(error);
    const msg = context ? `${context}: ${n.message}` : n.message;
    const e = new Error(msg);
    e.code = n.code;
    e.details = n.details;
    e.hint = n.hint;
    e.raw = n.raw;
    throw e;
}

// =========================
// EMAILS: Lecturas
// =========================

/**
 * Lista emails por event_id.
 * Ordenados por fecha (created_at) descendente por defecto.
 */
export async function listEmailsByEvent(eventId) {
    assertId(eventId, "eventId");
    const supabase = getSupabaseClient();

    // Seleccionamos campos estándar, y también info del contact si hace falta
    const { data, error } = await supabase
        .from(TABLE_NAME)
        .select("*, contacts:contact_id(name, email)")
        .eq("event_id", eventId)
        .order("created_at", { ascending: false });

    throwSupabase(error, "Error listando emails del evento");
    return data || [];
}

/**
 * Lista emails por contact_id.
 */
export async function listEmailsByContact(contactId) {
    assertId(contactId, "contactId");
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from(TABLE_NAME)
        .select("*")
        .eq("contact_id", contactId)
        .order("created_at", { ascending: false });

    throwSupabase(error, "Error listando emails del contacto");
    return data || [];
}

/**
 * Lista todos los emails con detalles del contacto y evento.
 * Útil para la pestaña global de Mails.
 */
export async function listAllEmailsWithDetails() {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from(TABLE_NAME)
        .select(`
            *,
            contacts:contact_id(name, email),
            events:event_id(client_name_legacy, date),
            venues:venue_id(name)
        `)
        // Excluir la comunicación interna al equipo (invites/mensajes a músicos/técnicos):
        // esta bandeja es solo para correos con contactos/clientes.
        .or("email_category.eq.external,email_category.is.null")
        .order("created_at", { ascending: false });

    throwSupabase(error, "Error listando todos los emails");
    return data || [];
}

/**
 * Cuenta cuántos emails hay sin leer (is_read = false).
 */
export async function getUnreadEmailsCount() {
    const supabase = getSupabaseClient();

    const { count, error } = await supabase
        .from(TABLE_NAME)
        .select("*", { count: "exact", head: true })
        .eq("is_read", false)
        .eq("direction", "inbound");

    throwSupabase(error, "Error contando emails sin leer");
    return count || 0;
}


/**
 * Obtiene un email individual
 */
export async function getEmailById(emailId) {
    assertId(emailId, "emailId");
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from(TABLE_NAME)
        .select("*")
        .eq("id", emailId)
        .single();

    throwSupabase(error, "Error leyendo email");
    return data;
}

// =========================
// EMAILS: Escrituras
// =========================

/**
 * Crea un registro en la tabla emails.
 * Esto NO envía el correo, solo guarda el registro (historial/draft).
 * Para enviar, usar sendEmail (que llamará a la Edge Function).
 * 
 * payload: { event_id, contact_id, direction, subject, body, status, ... }
 */
export async function createEmailRecord(payload) {
    const supabase = getSupabaseClient();

    // El campo user_id tiene default auth.uid() en BD, así que no es obligatorio enviarlo si hay sesión.
    // El usuario sugirió que user_id sobra si es visible para managers, 
    // pero lo dejamos por trazabilidad interna (quién envió qué), la BD lo gestiona sola.
    const { data, error } = await supabase
        .from(TABLE_NAME)
        .insert(payload)
        .select("*")
        .single();

    throwSupabase(error, "Error creando registro de email");
    return data;
}


/**
 * Actualiza un email (ej. cambiar status de 'draft' a 'sent').
 */
export async function updateEmailRecord(emailId, patch) {
    assertId(emailId, "emailId");
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from(TABLE_NAME)
        .update(patch)
        .eq("id", emailId)
        .select("*")
        .single();

    throwSupabase(error, "Error actualizando email");
    return data;
}

/**
 * Marca un email como leído.
 */
export async function markEmailAsRead(emailId) {
    return updateEmailRecord(emailId, { is_read: true });
}


/**
 * Borra un email.
 */
export async function deleteEmailRecord(emailId) {
    assertId(emailId, "emailId");
    const supabase = getSupabaseClient();

    const { error } = await supabase
        .from(TABLE_NAME)
        .delete()
        .eq("id", emailId);

    throwSupabase(error, "Error borrando email");
    return true;
}

/**
 * Vincula todos los emails de un hilo a un evento.
 */
/**
 * Vincula todos los emails de un hilo a un evento.
 * Si el evento ya tiene emails, une este hilo al thread existente del evento.
 */
export async function linkThreadToEvent(threadId, eventId) {
    assertId(threadId, "threadId");
    assertId(eventId, "eventId");
    const supabase = getSupabaseClient();

    // 1. Buscar si el evento ya tiene emails para obtener su thread_id principal
    const { data: existingEmails, error: listError } = await supabase
        .from(TABLE_NAME)
        .select("thread_id")
        .eq("event_id", eventId)
        .limit(1);

    if (listError) throwSupabase(listError, "Error verificando emails del evento");

    let targetThreadId = null;
    if (existingEmails && existingEmails.length > 0) {
        // Usamos el thread_id del primer email encontrado en ese evento
        targetThreadId = existingEmails[0].thread_id;
    }

    // 2. Preparar el update
    const patch = { event_id: eventId };
    if (targetThreadId) {
        // Merge: forzamos el ID del hilo existente
        patch.thread_id = targetThreadId;
    }

    // 3. Actualizar
    const { error } = await supabase
        .from(TABLE_NAME)
        .update(patch)
        .eq("thread_id", threadId);

    throwSupabase(error, "Error vinculando hilo a evento");
    return true;
}

/**
 * Vincula todos los emails de un hilo a un Contacto (Proveedor).
 */
export async function linkThreadToContact(threadId, contactId) {
    assertId(threadId, "threadId");
    assertId(contactId, "contactId");
    const supabase = getSupabaseClient();

    const { error } = await supabase
        .from(TABLE_NAME)
        .update({ contact_id: contactId, event_id: null })
        .eq("thread_id", threadId);

    throwSupabase(error, "Error vinculando hilo a contacto");
    return true;
}

/**
 * Vincula todos los emails de un hilo a un lugar.
 */
export async function linkThreadToVenue(threadId, venueId) {
    assertId(threadId, "threadId");
    assertId(venueId, "venueId");
    const supabase = getSupabaseClient();

    const { error } = await supabase
        .from(TABLE_NAME)
        .update({ venue_id: venueId, event_id: null })
        .eq("thread_id", threadId);

    throwSupabase(error, "Error vinculando hilo a lugar");
    return true;
}


// =========================
// SENDING (Placeholder for Phase 2)
// =========================

// =========================
// SENDING
// =========================

/**
 * Sube un archivo al bucket 'email-attachments' y devuelve su path.
 */
export async function uploadAttachment(file) {
    const supabase = getSupabaseClient();
    const fileName = `${Date.now()}_${file.name.replace(/[^a-zA-Z0-9.\-_]/g, '')}`;
    const { data, error } = await supabase.storage
        .from('email-attachments')
        .upload(fileName, file);

    if (error) {
        console.error("Error uploading attachment:", error);
        throw error;
    }
    return data.path; // Retorna el path para generar signedUrl en el backend
}

/**
 * Enviar email a través de Edge Function.
 * Permite adjuntar archivos (File objects) o adjuntos ya procesados.
 * 
 * @param {Object} params
 * @param {string|string[]} params.to
 * @param {string} params.subject
 * @param {string} params.body
 * @param {string} [params.from]
 * @param {Array<File|Object>} [params.attachments] - Array de objetos File (navegador) o {filename, content/path}
 */
export async function sendEmailViaFunction({ to, subject, body, from, attachments = [] }) {
    if (!EMAIL_SENDING_ENABLED) {
        throw new Error("El envío de correo desde el CRM está desactivado. Usa Outlook.");
    }
    const supabase = getSupabaseClient();

    // Procesar adjuntos: si son File, subirlos primero.
    const processedAttachments = await Promise.all(attachments.map(async (att) => {
        // Caso 1: Es un objeto File del navegador
        if (att instanceof File) {
            const path = await uploadAttachment(att);
            return {
                filename: att.name,
                storagePath: path // El backend generará la Signed URL
            };
        }
        // Caso 2: Objeto ya formateado (ej. reenvío o generado manualmente)
        return att;
    }));

    const { data, error } = await supabase.functions.invoke("send-email", {
        body: {
            to,
            subject,
            html: body.replace(/\n/g, '<br>'), // Simple text to HTML conversion
            from,
            attachments: processedAttachments
        }
    });

    if (error) throw error;
    return data;
}

/**
 * Envía un email INTERNO al equipo (músicos/técnicos), no a contactos/clientes.
 *
 * - Envía un único correo vía la Edge Function (Resend) a todos los `toEmails`.
 * - Opcionalmente adjunta una invitación .ics (icsAttachment ya formateado:
 *   { filename, content: base64, content_type }).
 * - Registra en `emails` una fila por destinatario con email_category='internal'
 *   para trazabilidad, sin contaminar la bandeja de clientes (Emailing.js filtra
 *   email_category='external').
 *
 * @param {Object} params
 * @param {string[]} params.toEmails            - Emails destinatarios (obligatorio, no vacío).
 * @param {string[]} [params.recipientMemberIds]- IDs de team_members (para el registro; opcional).
 * @param {string} params.subject
 * @param {string} params.body
 * @param {string} [params.from]
 * @param {string|null} [params.eventId]        - Evento asociado (para invites); null si suelto.
 * @param {Object|null} [params.icsAttachment]  - { filename, content, content_type } o null.
 * @returns {Promise<{sendResult:any, records:any[]}>}
 */
export async function sendInternalEmail({
    toEmails,
    recipientMemberIds = [],
    subject,
    body,
    from,
    eventId = null,
    icsAttachment = null,
}) {
    const recipients = (Array.isArray(toEmails) ? toEmails : [toEmails]).filter(Boolean);
    if (recipients.length === 0) throw new Error("No hay destinatarios con email.");
    if (!subject || !body) throw new Error("Asunto y mensaje son obligatorios.");

    // 1) Enviar (un solo correo a todos; los adjuntos .ics ya vienen como {filename,content,content_type})
    //    Usa el remitente interno verificado por defecto (se puede sobrescribir con `from`).
    const sendResult = await sendEmailViaFunction({
        to: recipients,
        subject,
        body,
        from: from || INTERNAL_FROM,
        attachments: icsAttachment ? [icsAttachment] : [],
    });

    // 2) Registrar una fila por miembro (o una genérica si no hay ids)
    const supabase = getSupabaseClient();
    const rowsMeta = recipientMemberIds.length > 0
        ? recipientMemberIds.map(id => ({ recipient_member_id: id }))
        : [{ recipient_member_id: null }];

    const payload = rowsMeta.map(meta => ({
        ...meta,
        event_id: eventId,
        email_category: "internal",
        direction: "outbound",
        status: "sent",
        subject,
        body,
    }));

    const { data: records, error } = await supabase
        .from(TABLE_NAME)
        .insert(payload)
        .select("*");

    // No hacemos fallar el envío si solo falla el registro (el correo ya salió)
    if (error) console.warn("[emailsService] Email interno enviado pero no registrado:", error.message);

    return { sendResult, records: records || [] };
}

// =========================
// NEEDS REVIEW (admin y manager)
// =========================

/**
 * Lista emails pendientes de revisión manual (processing_status = 'needs_review').
 * process-email detectó un evento ambiguo (p.ej. una wedding planner o finca
 * escribiendo desde la misma bandeja sobre varias bodas distintas) y prefirió
 * no adivinar. Cada fila trae en `review_data` todo lo que la IA extrajo más
 * los eventos candidatos con su score, para no tener que volver a llamarla.
 */
export async function listNeedsReviewEmails() {
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from(TABLE_NAME)
        .select("*, contacts:contact_id(name, surname, email, type)")
        .eq("processing_status", "needs_review")
        .order("created_at", { ascending: false });

    throwSupabase(error, "Error listando emails pendientes de revisión");
    return data || [];
}

/**
 * Cuenta cuántos emails están pendientes de revisión manual.
 */
export async function getNeedsReviewCount() {
    const supabase = getSupabaseClient();

    const { count, error } = await supabase
        .from(TABLE_NAME)
        .select("*", { count: "exact", head: true })
        .eq("processing_status", "needs_review");

    throwSupabase(error, "Error contando emails pendientes de revisión");
    return count || 0;
}

/**
 * Resuelve un email marcado como needs_review, vía la Edge Function
 * `resolve-email-review`. Esa función valida en el servidor que quien llama
 * es admin o manager (no basta con ocultar el botón en el cliente).
 *
 * @param {Object} params
 * @param {string} params.emailId
 * @param {"assign"|"create_new"|"dismiss"} params.action
 * @param {string} [params.eventId] - obligatorio si action === "assign"
 */
export async function resolveEmailReview({ emailId, action, eventId }) {
    assertId(emailId, "emailId");
    const supabase = getSupabaseClient();

    const { data, error } = await supabase.functions.invoke("resolve-email-review", {
        body: { email_id: emailId, action, event_id: eventId },
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
}

/**
 * Lista emails sin leer para un equipo entero.
 */
export async function listUnreadEmailsByTeam(teamId, limit = 10) {
    assertId(teamId, "teamId");
    const supabase = getSupabaseClient();

    const { data, error } = await supabase
        .from(TABLE_NAME)
        .select(`
            *,
            contacts:contact_id(name),
            events!inner(team_id, client_name_legacy)
        `)
        .eq("events.team_id", teamId)
        .eq("is_read", false)
        .eq("direction", "inbound")
        .order("created_at", { ascending: false })
        .limit(limit);

    throwSupabase(error, "Error listando emails sin leer del equipo");
    return data || [];
}
